import { ProductPageTemplate } from "@/components/ProductPageTemplate";
import { Car, Shield, Users, Clock, MapPin, Wrench } from "lucide-react";
import {getImageUrl} from '@/lib/utils';

const FleetInsurance = () => {
  const pageData = {
    // SEO Meta Data
    title: "Fleet Insurance UAE | Commercial Vehicle Insurance Dubai | YallaCompare",
    metaDescription: "Get comprehensive fleet insurance in UAE. Compare commercial vehicle insurance quotes in Dubai, Abu Dhabi & UAE. Protect your business vehicles with competitive rates.",

    // Hero Section
    heroTitle: "Comprehensive Fleet Insurance for UAE Businesses",
    heroSubtitle: "Commercial Vehicle Protection",
    heroDescription: "Comprehensive insurance coverage for your business vehicles across the UAE. Protect your fleet with competitive rates and extensive coverage options.",
    heroImage: getImageUrl("fleet-icon.png"),

    // Key Benefits
    keyBenefits: [
      "Comprehensive coverage for all vehicle types",
      "UAE-wide protection across all Emirates",
      "Fleet management and tracking benefits",
      "24/7 roadside assistance and support",
      "Competitive group rates for multiple vehicles",
      "Flexible coverage options and deductibles"
    ],

    // Features Section
    features: [
      {
        title: "Comprehensive Vehicle Coverage",
        description: "Full protection for cars, trucks, vans, and commercial vehicles across your entire fleet.",
        icon: <Car className="h-6 w-6 text-blue-600" />
      },
      {
        title: "UAE-Wide Protection",
        description: "Coverage across all Emirates including Dubai, Abu Dhabi, Sharjah, and Northern Emirates.",
        icon: <MapPin className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Fleet Management Benefits",
        description: "Centralized policy management, bulk discounts, and streamlined claims processing.",
        icon: <Users className="h-6 w-6 text-blue-600" />
      },
      {
        title: "24/7 Roadside Assistance",
        description: "Emergency support, towing services, and breakdown assistance across the UAE.",
        icon: <Wrench className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Driver Protection",
        description: "Coverage for multiple drivers and temporary driver additions for business flexibility.",
        icon: <Shield className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Business Continuity",
        description: "Replacement vehicle options and quick claim settlements to minimize business disruption.",
        icon: <Clock className="h-6 w-6 text-blue-600" />
      }
    ],

    // Coverage Details
    coverageDetails: [
      {
        title: "Vehicle Protection",
        description: "Comprehensive coverage for all types of commercial vehicles",
        included: [
          "Third-party liability (mandatory in UAE)",
          "Comprehensive vehicle damage coverage",
          "Theft and vandalism protection",
          "Fire and natural disaster coverage",
          "Windscreen and glass damage"
        ]
      },
      {
        title: "Driver & Passenger Coverage",
        description: "Protection for drivers and passengers in your fleet",
        included: [
          "Personal accident coverage for drivers",
          "Medical expenses for occupants",
          "Roadside assistance and towing",
          "Replacement vehicle during repairs",
          "Legal liability coverage"
        ]
      }
    ],

    // Why Choose Us
    whyChooseUs: [
      {
        title: "Legal Compliance",
        description: "Meet UAE mandatory insurance requirements for all commercial vehicles and avoid penalties"
      },
      {
        title: "Cost Efficiency",
        description: "Bulk fleet discounts and centralized management reduce overall insurance costs"
      },
      {
        title: "Business Protection",
        description: "Protect your business assets and ensure operational continuity with comprehensive coverage"
      },
      {
        title: "Risk Management",
        description: "Professional risk assessment and tailored coverage for your specific fleet requirements"
      }
    ],

    // FAQ Section
    faqs: [
      {
        question: "What types of vehicles can be covered under fleet insurance?",
        answer: "Fleet insurance covers cars, vans, trucks, buses, motorcycles, and specialized commercial vehicles. Coverage is available for fleets of 5 or more vehicles in the UAE."
      },
      {
        question: "Is fleet insurance mandatory in the UAE?",
        answer: "Yes, third-party liability insurance is mandatory for all vehicles in the UAE. Comprehensive fleet insurance provides additional protection beyond the legal minimum."
      },
      {
        question: "How are fleet insurance premiums calculated?",
        answer: "Premiums depend on vehicle types, driver profiles, claims history, fleet size, and usage patterns. Larger fleets typically receive better rates."
      },
      {
        question: "Can I add or remove vehicles from my fleet policy?",
        answer: "Yes, fleet policies offer flexibility to add or remove vehicles during the policy term with appropriate premium adjustments."
      },
      {
        question: "What happens if one of my drivers has an accident?",
        answer: "Your policy covers accidents by authorized drivers. Claims are processed centrally, and coverage includes vehicle repairs, third-party liability, and medical expenses."
      }
    ],

    // CTA Section
    ctaTitle: "Protect Your Fleet Today",
    ctaDescription: "Get comprehensive fleet insurance coverage for your business vehicles. Compare quotes from leading UAE insurers and secure your fleet protection in minutes.",
    primaryCta: "Get Fleet Insurance Quote",
    secondaryCta: "Compare All Insurance Products",

    // Product ID for quote form
    productId: "fleet-insurance"
  };

  return <ProductPageTemplate {...pageData} />;
};

export default FleetInsurance;
